import React, { useState } from 'react';
import { useCalculatorContext } from '../contexts';
import CalculatorDropdown from './CalculatorDropdown';
import CalculatorInput from './CalculatorInput';
import { EditIcon } from '@/components/DesignSystem/Icons';
import tagularEvent from '@/utils/tagular/tagularEvent';
import { ElementClicked, TagularWebElement } from '@/types/Tagular';
import uuid from '@/utils/uuid';

interface CalculatorFormPanelProps {
  className?: string;
  showEditButton?: boolean;
  initiallyEditable?: boolean;
  showSubmitButton?: boolean;
  onSubmit?: () => void;
  viewCorrelationId?: string;
}

const CalculatorFormPanel: React.FC<CalculatorFormPanelProps> = ({
  className = '',
  showEditButton = false,
  initiallyEditable = false,
  showSubmitButton = false,
  onSubmit,
  viewCorrelationId,
}) => {
  // Local edit state - starts with initiallyEditable value
  const [isEditMode, setIsEditMode] = useState(initiallyEditable);
  // Shared correlation ID for edit/update tracking events
  const [editUpdateCorrelationId, setEditUpdateCorrelationId] = useState<
    string | null
  >(null);

  const {
    state,
    occupations,
    config,
    isFormComplete,
    triggerDataFetch,
    isCalculating,
  } = useCalculatorContext();
  const formState = state.formState;

  const handleEditClick = () => {
    // Generate a new correlation ID for this edit session
    const correlationId = uuid();
    setEditUpdateCorrelationId(correlationId);

    // Fire ElementClicked tracking event
    const elementClickedPayload: TagularWebElement = {
      elementType: 'button',
      location: config?.tracking?.location || '',
      name: 'edit-cta',
      text: 'Edit Details',
    };

    tagularEvent('ElementClicked', {
      webElement: elementClickedPayload,
      correlationId,
    });

    setIsEditMode(!isEditMode);
  };

  const handleUpdate = () => {
    // Fire ElementClicked tracking event using the same correlation ID from edit
    const elementClickedPayload: TagularWebElement = {
      elementType: 'BUTTON',
      location: config?.tracking?.location || '',
      name: 'update-results',
      text: 'Update Results',
    };

    tagularEvent('ElementClicked', {
      webElement: elementClickedPayload,
      correlationId: editUpdateCorrelationId || uuid(), // Use shared correlation ID or fallback to new one
    });

    // Trigger data fetch to update context and re-fetch programs
    if (isFormComplete() && triggerDataFetch) {
      triggerDataFetch();
    }

    // Close edit mode and clear the correlation ID
    setIsEditMode(false);
    setEditUpdateCorrelationId(null);
  };

  // Transform occupations to dropdown options format
  const occupationOptions = (occupations || []).map((occupation: any) => ({
    id: occupation.id || occupation.bls_code,
    name: occupation.title || occupation.name,
    slug: occupation.bls_code || occupation.id?.toString(),
    ...occupation,
  }));

  // Get fields from config, fallback to empty array if not available
  const configFields = config?.fields || [];

  // Function to get options for a field
  const getFieldOptions = (field: any) => {
    // If field has static options, use those
    if (field.options && field.options.length > 0) {
      return field.options;
    }

    // For dynamic dropdowns (DCS fields), use formState options
    if (field.key === 'degree') {
      return formState?.options?.degree || [];
    } else if (field.key === 'category') {
      return formState?.options?.category || [];
    } else if (field.key === 'subject') {
      return formState?.options?.subject || [];
    }

    // For occupation-related fields, use occupations data
    if (field.key === 'currentRole' || field.key === 'targetRole') {
      return occupationOptions;
    }

    return [];
  };

  // Group fields by text fields (section headers)
  const groupFieldsBySection = (fields: any[]) => {
    const groups: any[][] = [];
    let currentGroup: any[] = [];

    for (const field of fields) {
      if (field.type === 'text') {
        // If we have a current group, push it to groups
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
        }
        // Start a new group with the text field
        currentGroup = [field];
      } else {
        // Add non-text fields to the current group
        currentGroup.push(field);
      }
    }

    // Don't forget the last group
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  };

  const fieldGroups = groupFieldsBySection(configFields);

  // Render individual field based on type
  const renderField = (field: any) => {
    if (field.type === 'text') {
      return (
        <div className='font-bold text-sm text-gray-900 pb-1'>
          {field.label}
        </div>
      );
    } else if (field.type === 'select') {
      return (
        <CalculatorDropdown
          key={field.key}
          fieldKey={field.key}
          label={field.label}
          placeholder={field.placeholder}
          options={getFieldOptions(field)}
          dependsOn={field.dependsOn || []}
          isEditable={isEditMode}
          viewCorrelationId={viewCorrelationId}
        />
      );
    } else {
      // Handle input fields (text, number, etc.)
      return (
        <CalculatorInput
          key={field.key}
          fieldKey={field.key}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type}
          prefix={field.prefix}
          helpText={field.helpText}
          required={field.required}
          validation={field.validation}
          formatting={field.formatting}
          isEditable={isEditMode}
          viewCorrelationId={viewCorrelationId}
        />
      );
    }
  };

  return (
    <div className={`w-full bg-gray-25 rounded-lg ${className}`}>
      {/* Header with Edit Button */}
      {showEditButton && (
        <div className='flex justify-between items-center mb-4'>
          <div className='flex flex-col gap-2'>
            <span className='text-base md:text-lg font-bold text-black'>
              Your Information
            </span>
            {isEditMode && (
              <span className='text-base text-gray-600'>
                Edit your details below to update your results.
              </span>
            )}
          </div>

          {!isEditMode && (
            <button
              onClick={handleEditClick}
              className='font-normal flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors'
            >
              <EditIcon />
              <span className='font-medium md:flex hidden'>Edit Details</span>
            </button>
          )}

          {isEditMode && (
            <div className='hidden md:flex justify-end'>
              <button
                className={`px-6 py-3 rounded-lg font-semibold text-sm transition-colors ${
                  isFormComplete() && !isCalculating
                    ? 'bg-secondary-700 text-white hover:bg-secondary-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                onClick={handleUpdate}
                disabled={!isFormComplete() || isCalculating}
              >
                {isCalculating ? 'Updating...' : 'Update Results'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Form Fields - Grouped by Sections */}
      <div className='space-y-3'>
        {fieldGroups.map((group, groupIndex) => {
          const hasTextHeader = group[0]?.type === 'text';
          const textField = hasTextHeader ? group[0] : null;
          const fieldsToRender = hasTextHeader ? group.slice(1) : group;

          return (
            <div key={groupIndex} className='w-full'>
              {/* Render text field header if present */}
              {textField && renderField(textField)}

              {/* Render other fields in the group */}
              {fieldsToRender.length > 0 && (
                <div
                  className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${config?.maxColumns || 3} gap-2 text-xs md:text-sm items-end`}
                >
                  {fieldsToRender.map((field: any) => renderField(field))}
                  {/* Submit Button - only render in the last group */}
                  {showSubmitButton &&
                    !isCalculating &&
                    groupIndex === fieldGroups.length - 1 && (
                      <button
                        onClick={onSubmit}
                        className={`max-w-full h-11 px-4 py-2 rounded-lg font-semibold text-white text-sm transition-all duration-200 ${
                          isFormComplete()
                            ? 'bg-secondary-700 hover:bg-secondary-800 hover:shadow-lg cursor-pointer'
                            : 'bg-gray-400 cursor-not-allowed'
                        }`}
                        disabled={!isFormComplete()}
                      >
                        {config?.ctaText}
                      </button>
                    )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showEditButton && isEditMode && (
        <div className='flex md:hidden mt-4'>
          <button
            className={`px-6 py-3 rounded-lg font-semibold text-sm transition-colors ${
              isFormComplete() && !isCalculating
                ? 'bg-secondary-700 text-white hover:bg-secondary-800 w-full'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleUpdate}
            disabled={!isFormComplete() || isCalculating}
          >
            {isCalculating ? 'Updating...' : 'Update Results'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CalculatorFormPanel;
